import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Crown, CheckCircle, TrendUp, Rocket, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PremiumPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [autoRenew, setAutoRenew] = useState(true);

  useEffect(() => {
    if (user?.role === 'professional') {
      fetchSubscription();
    }
    api.get('/api/subscriptions/plans').then((r) => {
      const list = r.data.plans || [];
      setPlans(list);
      if (list.length && !selectedPlan) setSelectedPlan(list[0].code);
    }).catch(() => {});
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/api/subscriptions/status');
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Devi essere loggato per sottoscrivere');
      navigate('/login');
      return;
    }
    if (user.role !== 'professional') {
      toast.error('Solo i professionisti possono sottoscrivere Premium');
      return;
    }

    setLoading(true);
    try {
      const endpoint = autoRenew ? '/api/subscriptions/checkout-recurring' : '/api/subscriptions/checkout';
      const response = await api.post(endpoint, {
        plan: selectedPlan,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Errore durante il checkout';
      toast.error(detail);
      if (detail.includes('profile')) {
        toast.info('Devi prima creare il tuo profilo professionista');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Sei sicuro di voler annullare l\'abbonamento? Rimarrà attivo fino alla fine del periodo corrente.')) {
      return;
    }
    try {
      await api.post('/api/subscriptions/cancel');
      toast.success('Abbonamento annullato. Rimarrà attivo fino alla scadenza.');
      fetchSubscription();
    } catch (error) {
      toast.error('Errore durante l\'annullamento');
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) { toast.error('Seleziona un piano superiore'); return; }
    setLoading(true);
    try {
      const r = await api.post('/api/subscriptions/upgrade', {
        plan: selectedPlan,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        toast.info(`Pagamento differenza: €${r.data.delta_eur}`);
        window.location.href = r.data.checkout_url;
      } else if (r.data.upgraded) {
        toast.success('Upgrade completato senza costi aggiuntivi');
        fetchSubscription();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upgrade');
    } finally { setLoading(false); }
  };

  const isPremium = subscription?.is_premium;

  const content = (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Crown size={20} weight="fill" />
            <span className="font-medium text-sm">VIVALINK PREMIUM</span>
          </div>
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground mb-6">
            Fai Crescere la Tua<br />
            <span className="text-primary">Attività Professionale</span>
          </h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            Ottieni maggiore visibilità, badge verificato e strumenti avanzati per attirare più clienti.
          </p>
        </div>

        {isPremium && (
          <>
            <Card className="neumorphism rounded-2xl p-6 mb-8 border-l-4 border-l-accent" data-testid="premium-status-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                    <Crown size={24} className="text-white" weight="fill" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-xl">Sei Premium!</p>
                    <p className="text-sm text-foreground/70">
                      Piano: <strong>{subscription.plan}</strong> • Scade il {new Date(subscription.expires_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={handleCancel} data-testid="cancel-subscription-btn">
                  Annulla rinnovo
                </Button>
              </div>
            </Card>

            {/* Upgrade block */}
            <div className="mb-8" data-testid="upgrade-block">
              <h2 className="font-heading font-medium text-2xl mb-3">Passa a un piano superiore</h2>
              <p className="text-sm text-foreground/70 mb-4">Paghi solo la differenza pro-rata sui giorni residui — la scadenza rimane la stessa.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.filter((p) => p.code !== subscription.plan).map((plan) => (
                  <Card key={plan.code} className={`neumorphism rounded-2xl p-5 cursor-pointer ${selectedPlan === plan.code ? 'ring-2 ring-primary' : ''}`}
                        onClick={() => setSelectedPlan(plan.code)}
                        data-testid={`upgrade-plan-${plan.code}`}>
                    <div className="text-sm font-medium mb-1">{plan.label}</div>
                    <div className="text-2xl font-heading font-bold">€{plan.eur}<span className="text-sm text-foreground/60 font-normal">/{plan.period === 'year' ? 'anno' : plan.period === '6month' ? '6 mesi' : 'mese'}</span></div>
                    {plan.for === 'centro' && <Badge className="bg-primary text-white mt-2 text-[10px]">Centri</Badge>}
                  </Card>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Button
                  size="lg"
                  className="rounded-full px-10"
                  disabled={loading || !selectedPlan}
                  onClick={handleUpgrade}
                  data-testid="do-upgrade-btn"
                >
                  {loading ? 'Elaborazione…' : 'Calcola upgrade e paga la differenza'}
                </Button>
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <Card className="neumorphism rounded-2xl p-6" data-testid="benefit-visibility">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <TrendUp size={24} className="text-primary" weight="duotone" />
            </div>
            <h3 className="font-heading font-medium text-xl mb-2">Visibilità Prioritaria</h3>
            <p className="text-foreground/70">
              Il tuo profilo apparirà in cima ai risultati di ricerca, aumentando fino al 5x le visite.
            </p>
          </Card>

          <Card className="neumorphism rounded-2xl p-6" data-testid="benefit-badge">
            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck size={24} className="text-accent" weight="duotone" />
            </div>
            <h3 className="font-heading font-medium text-xl mb-2">Badge Premium</h3>
            <p className="text-foreground/70">
              Ottieni il badge &quot;Certified Premium&quot; che ispira fiducia e trasmette qualità.
            </p>
          </Card>

          <Card className="neumorphism rounded-2xl p-6" data-testid="benefit-analytics">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
              <Rocket size={24} className="text-accent" weight="duotone" />
            </div>
            <h3 className="font-heading font-medium text-xl mb-2">Analytics Avanzate</h3>
            <p className="text-foreground/70">
              Traccia visualizzazioni, conversioni e ottimizza il tuo profilo con dati dettagliati.
            </p>
          </Card>
        </div>

        {!isPremium && (
          <>
            <div className="text-center mb-8">
              <h2 className="font-heading font-medium text-3xl mb-4">Scegli il Tuo Piano</h2>
              <p className="text-foreground/70">Risparmia scegliendo il periodo più lungo</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-8" data-testid="plans-grid">
              {plans.map((plan) => {
                const active = selectedPlan === plan.code;
                const periodLabel = plan.period === 'year' ? '/anno' : plan.period === '6month' ? '/6 mesi' : '/mese';
                const highlight = plan.code === 'pro_annual';
                const isCentro = plan.for === 'centro' || plan.code === 'pro_annual_centro';
                return (
                  <Card
                    key={plan.code}
                    className={`neumorphism rounded-2xl p-6 cursor-pointer transition-transform duration-200 relative ${active ? 'ring-2 ring-primary -translate-y-1' : ''}`}
                    onClick={() => setSelectedPlan(plan.code)}
                    data-testid={`plan-${plan.code}`}
                  >
                    {highlight && (
                      <div className="absolute -top-3 right-4">
                        <Badge className="bg-accent text-white">Miglior valore</Badge>
                      </div>
                    )}
                    {isCentro && (
                      <div className="absolute -top-3 left-4">
                        <Badge className="bg-primary text-white">Riservato Centri</Badge>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-medium text-xl">{plan.label}</h3>
                      {active && <Badge className="bg-primary text-white">Selezionato</Badge>}
                    </div>
                    <div className="mb-6">
                      <span className="font-heading font-bold text-4xl">€{plan.eur}</span>
                      <span className="text-foreground/70">{periodLabel}</span>
                    </div>
                    <ul className="space-y-3 mb-6">
                      {(isCentro ? [
                        'Visibilità multi-categoria (tutte le prestazioni)',
                        'Pacchetto crediti dedicato ai Centri',
                        'Gestione multi-operatore inclusa',
                        'Dashboard analytics avanzata',
                        'Supporto prioritario',
                      ] : [
                        'Visibilità prioritaria nei risultati',
                        'Badge PRO verificato',
                        'Dashboard analytics avanzata',
                        'Notifiche in-app in tempo reale',
                        'Supporto prioritario',
                      ]).map((feat, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <CheckCircle size={20} className="text-primary flex-shrink-0" weight="fill" />
                          <span className="text-sm">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                );
              })}
            </div>

            <div className="text-center">
              <div className="inline-flex items-center gap-3 mb-4 px-4 py-3 rounded-2xl bg-muted/40 border border-border" data-testid="autorenew-toggle-wrap">
                <label htmlFor="auto-renew" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="auto-renew"
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    className="w-4 h-4 accent-primary"
                    data-testid="autorenew-toggle"
                  />
                  <span className="text-sm">
                    <strong>Rinnovo automatico</strong>
                    <span className="text-foreground/60 text-xs ml-2">
                      {autoRenew ? 'Non perderai visibilità; puoi annullare in qualunque momento' : 'Pagamento singolo per un periodo; nessun rinnovo'}
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <Button
                  size="lg"
                  className="rounded-full px-12"
                  onClick={handleSubscribe}
                  disabled={loading || !selectedPlan}
                  data-testid="subscribe-btn"
                >
                  {loading ? 'Elaborazione...' : `${autoRenew ? 'Attiva con rinnovo' : 'Attiva'} ${plans.find((p) => p.code === selectedPlan)?.label || 'Abbonamento'}`}
                </Button>
              </div>
              <p className="text-sm text-foreground/60 mt-4">
                Pagamento sicuro tramite Stripe • {autoRenew ? 'Cancellazione con validità fino alla scadenza naturale' : 'Rinnovo manuale — nessun addebito automatico'}
              </p>
            </div>
          </>
        )}
      </div>
  );

  if (isAuthenticated) {
    return <DashboardLayout>{content}</DashboardLayout>;
  }
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {content}
    </div>
  );
};

export default PremiumPage;
